"use client";

import { c as _c } from "react/compiler-runtime";
import { addHours, format } from "date-fns";
import { Activity, AlertTriangle, Clock, Gauge, Layers, RefreshCw, Zap } from "lucide-react";
import { useState } from "react";
import { Bar, BarChart, CartesianGrid, Cell, XAxis, YAxis } from "recharts";
import { Badge } from "@/lib/components/badge";
import { Button } from "@/lib/components/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/lib/components/card";
import { ChartContainer, ChartTooltip, ChartTooltipContent, type ChartConfig } from "@/lib/components/chart";
import { useApi } from "@/lib/fetch-client";
import { formatCompact, hasEnoughRequestsForStats, hasEnoughTtftSamplesForStats, MIN_REQUESTS_FOR_STATS } from "@/lib/provider-stats";
import { cn } from "@/lib/utils";
import { deriveStabilityMetrics } from "@llmgateway/shared";
import { getProviderIcon } from "@llmgateway/shared/components";
import { formatNumber } from "@llmgateway/shared/number-format";
import type { paths } from "@/lib/api/v1";
type ActiveMetric = "tokens" | "requests" | "errors" | "latency";
type UptimeResponse = paths["/internal/models/{modelId}/uptime"]["get"]["responses"]["200"]["content"]["application/json"];
type UptimeProvider = UptimeResponse["providers"][number];
const chartConfigs: Record<ActiveMetric, ChartConfig> = {
  tokens: {
    totalTokens: {
      label: "Tokens",
      color: "hsl(32 95% 44%)"
    }
  },
  requests: {
    logsCount: {
      label: "Requests",
      color: "hsl(221 83% 53%)"
    },
    cachedCount: {
      label: "Cached",
      color: "hsl(142 71% 45%)"
    }
  },
  errors: {
    clientErrorsCount: {
      label: "Client",
      color: "hsl(38 92% 50%)"
    },
    gatewayErrorsCount: {
      label: "Gateway",
      color: "hsl(262 83% 58%)"
    },
    upstreamErrorsCount: {
      label: "Upstream",
      color: "hsl(0 84% 60%)"
    }
  },
  latency: {
    avgTtft: {
      label: "Avg TTFT (ms)",
      color: "hsl(262 83% 58%)"
    },
    avgDuration: {
      label: "Avg Duration (ms)",
      color: "hsl(221 83% 53%)"
    }
  }
};

// Error sources are disjoint, so they stack into the hour's total. The other
// multi-series metrics overlap (cached is a subset of requests, TTFT is part of
// the duration) and are drawn side by side instead.
const stackedMetrics = new Set<ActiveMetric>(["errors"]);
const metricTabs: {
  key: ActiveMetric;
  label: string;
}[] = [{
  key: "tokens",
  label: "Tokens"
}, {
  key: "requests",
  label: "Requests"
}, {
  key: "errors",
  label: "Errors"
}, {
  key: "latency",
  label: "Latency"
}];
function formatHourRange(timestamp: string): string {
  const start = new Date(timestamp);
  return `${format(start, "MMM d, HH:mm")} – ${format(addHours(start, 1), "HH:mm")}`;
}
function ProviderUptimeCard(t0) {
  const $ = _c(121);
  const {
    provider
  } = t0;
  const [activeMetric, setActiveMetric] = useState("tokens");
  let t1;
  if ($[0] !== provider.providerId) {
    t1 = getProviderIcon(provider.providerId);
    $[0] = provider.providerId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const ProviderIcon = t1;
  const config = chartConfigs[activeMetric];
  let t2;
  if ($[2] !== config) {
    t2 = Object.keys(config);
    $[2] = config;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const dataKeys = t2;
  let t3;
  if ($[4] !== activeMetric) {
    t3 = stackedMetrics.has(activeMetric);
    $[4] = activeMetric;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const stacked = t3;
  const currentHourIndex = provider.points.length - 1;
  const currentHourTimestamp = provider.points[currentHourIndex]?.timestamp;
  let t4;
  if ($[6] !== provider.logsCount) {
    t4 = hasEnoughRequestsForStats(provider.logsCount);
    $[6] = provider.logsCount;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const hasEnoughData = t4;
  let t5;
  if ($[8] !== hasEnoughData || $[9] !== provider.ttftCount) {
    t5 = hasEnoughData && hasEnoughTtftSamplesForStats(provider.ttftCount);
    $[8] = hasEnoughData;
    $[9] = provider.ttftCount;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const hasEnoughTtftData = t5;
  let T0;
  let T1;
  let T2;
  let errorRate;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[11] !== ProviderIcon || $[12] !== hasEnoughData || $[13] !== hasEnoughTtftData || $[14] !== provider.avgDuration || $[15] !== provider.avgTtft || $[16] !== provider.clientErrorsCount || $[17] !== provider.gatewayErrorsCount || $[18] !== provider.logsCount || $[19] !== provider.providerId || $[20] !== provider.providerName || $[21] !== provider.tokensPerSecond || $[22] !== provider.totalTokens || $[23] !== provider.upstreamErrorsCount || $[24] !== provider.uptime) {
    const stability = deriveStabilityMetrics({
      logsCount: provider.logsCount,
      clientErrorsCount: provider.clientErrorsCount,
      gatewayErrorsCount: provider.gatewayErrorsCount,
      upstreamErrorsCount: provider.upstreamErrorsCount
    });
    errorRate = stability.errorRate !== null ? Math.round(stability.errorRate * 10) / 10 : null;
    const uptimeColor = !hasEnoughData || provider.uptime === null ? "text-muted-foreground" : provider.uptime >= 99.5 ? "text-green-600 dark:text-green-500" : provider.uptime >= 95 ? "text-yellow-600 dark:text-yellow-500" : "text-red-600 dark:text-red-500";
    T2 = Card;
    T1 = CardHeader;
    t13 = "space-y-4 pb-2";
    let t15;
    if ($[38] !== ProviderIcon) {
      t15 = <div className="rounded-md border border-border/60 bg-muted/40 p-2">{ProviderIcon ? <ProviderIcon className="h-5 w-5 shrink-0 dark:text-white" /> : <Activity className="h-5 w-5 shrink-0" />}</div>;
      $[38] = ProviderIcon;
      $[39] = t15;
    } else {
      t15 = $[39];
    }
    let t16;
    if ($[40] !== provider.providerId) {
      t16 = <Badge variant="outline" className="text-[10px] uppercase">{provider.providerId}</Badge>;
      $[40] = provider.providerId;
      $[41] = t16;
    } else {
      t16 = $[41];
    }
    let t17;
    if ($[42] !== provider.providerName || $[43] !== t16) {
      t17 = <CardTitle className="text-base flex items-center gap-2">{provider.providerName}{t16}</CardTitle>;
      $[42] = provider.providerName;
      $[43] = t16;
      $[44] = t17;
    } else {
      t17 = $[44];
    }
    let t18;
    if ($[45] !== hasEnoughData) {
      t18 = !hasEnoughData && ` · stats hidden until ${formatCompact(MIN_REQUESTS_FOR_STATS)} requests`;
      $[45] = hasEnoughData;
      $[46] = t18;
    } else {
      t18 = $[46];
    }
    let t19;
    if ($[47] !== t18) {
      t19 = <CardDescription className="text-xs">Last 24 hours · hourly{t18}</CardDescription>;
      $[47] = t18;
      $[48] = t19;
    } else {
      t19 = $[48];
    }
    let t20;
    if ($[49] !== t17 || $[50] !== t19) {
      t20 = <div>{t17}{t19}</div>;
      $[49] = t17;
      $[50] = t19;
      $[51] = t20;
    } else {
      t20 = $[51];
    }
    let t21;
    if ($[52] !== t15 || $[53] !== t20) {
      t21 = <div className="flex items-center gap-3">{t15}{t20}</div>;
      $[52] = t15;
      $[53] = t20;
      $[54] = t21;
    } else {
      t21 = $[54];
    }
    let t22;
    if ($[55] !== hasEnoughData || $[56] !== provider.uptime || $[57] !== uptimeColor) {
      t22 = provider.uptime !== null && <div className="flex flex-col items-end"><span className={cn("text-2xl font-bold tabular-nums", uptimeColor)}>{hasEnoughData ? `${provider.uptime.toFixed(1)}%` : "\u2014"}</span><span className="text-[10px] uppercase tracking-wide text-muted-foreground">Uptime</span></div>;
      $[55] = hasEnoughData;
      $[56] = provider.uptime;
      $[57] = uptimeColor;
      $[58] = t22;
    } else {
      t22 = $[58];
    }
    if ($[59] !== t21 || $[60] !== t22) {
      t14 = <div className="flex flex-wrap items-start justify-between gap-3">{t21}{t22}</div>;
      $[59] = t21;
      $[60] = t22;
      $[61] = t14;
    } else {
      t14 = $[61];
    }
    t9 = "grid grid-cols-2 sm:grid-cols-4 gap-3 pt-1";
    let t23;
    if ($[62] !== provider.totalTokens) {
      t23 = formatCompact(provider.totalTokens);
      $[62] = provider.totalTokens;
      $[63] = t23;
    } else {
      t23 = $[63];
    }
    let t24;
    if ($[64] !== provider.logsCount) {
      t24 = formatCompact(provider.logsCount);
      $[64] = provider.logsCount;
      $[65] = t24;
    } else {
      t24 = $[65];
    }
    const t25 = `${t24} requests`;
    if ($[66] !== t23 || $[67] !== t25) {
      t10 = <Stat icon={Layers} label="Tokens" value={t23} sub={t25} />;
      $[66] = t23;
      $[67] = t25;
      $[68] = t10;
    } else {
      t10 = $[68];
    }
    const t26 = hasEnoughTtftData && provider.avgTtft !== null ? `${provider.avgTtft}ms` : "\u2014";
    const t27 = hasEnoughData && provider.avgDuration !== null ? `${provider.avgDuration}ms duration` : undefined;
    if ($[69] !== t26 || $[70] !== t27) {
      t11 = <Stat icon={Clock} label="Avg TTFT" value={t26} sub={t27} />;
      $[69] = t26;
      $[70] = t27;
      $[71] = t11;
    } else {
      t11 = $[71];
    }
    let t28;
    if ($[72] !== hasEnoughData || $[73] !== provider.tokensPerSecond) {
      t28 = hasEnoughData && provider.tokensPerSecond !== null ? `${formatNumber(provider.tokensPerSecond)} t/s` : "\u2014";
      $[72] = hasEnoughData;
      $[73] = provider.tokensPerSecond;
      $[74] = t28;
    } else {
      t28 = $[74];
    }
    if ($[75] !== t28) {
      t12 = <Stat icon={Gauge} label="Throughput" value={t28} />;
      $[75] = t28;
      $[76] = t12;
    } else {
      t12 = $[76];
    }
    T0 = Stat;
    t6 = AlertTriangle;
    t7 = "Errors";
    t8 = formatCompact(stability.errorsCount);
    $[11] = ProviderIcon;
    $[12] = hasEnoughData;
    $[13] = hasEnoughTtftData;
    $[14] = provider.avgDuration;
    $[15] = provider.avgTtft;
    $[16] = provider.clientErrorsCount;
    $[17] = provider.gatewayErrorsCount;
    $[18] = provider.logsCount;
    $[19] = provider.providerId;
    $[20] = provider.providerName;
    $[21] = provider.tokensPerSecond;
    $[22] = provider.totalTokens;
    $[23] = provider.upstreamErrorsCount;
    $[24] = provider.uptime;
    $[25] = T0;
    $[26] = T1;
    $[27] = T2;
    $[28] = errorRate;
    $[29] = t10;
    $[30] = t11;
    $[31] = t12;
    $[32] = t13;
    $[33] = t14;
    $[34] = t6;
    $[35] = t7;
    $[36] = t8;
    $[37] = t9;
  } else {
    T0 = $[25];
    T1 = $[26];
    T2 = $[27];
    errorRate = $[28];
    t10 = $[29];
    t11 = $[30];
    t12 = $[31];
    t13 = $[32];
    t14 = $[33];
    t6 = $[34];
    t7 = $[35];
    t8 = $[36];
    t9 = $[37];
  }
  let t15;
  if ($[77] !== errorRate || $[78] !== hasEnoughData) {
    t15 = hasEnoughData && errorRate !== null ? `${errorRate.toFixed(1)}% rate` : undefined;
    $[77] = errorRate;
    $[78] = hasEnoughData;
    $[79] = t15;
  } else {
    t15 = $[79];
  }
  let t16;
  if ($[80] !== T0 || $[81] !== t15 || $[82] !== t6 || $[83] !== t7 || $[84] !== t8) {
    t16 = <T0 icon={t6} label={t7} value={t8} sub={t15} />;
    $[80] = T0;
    $[81] = t15;
    $[82] = t6;
    $[83] = t7;
    $[84] = t8;
    $[85] = t16;
  } else {
    t16 = $[85];
  }
  let t17;
  if ($[86] !== t10 || $[87] !== t11 || $[88] !== t12 || $[89] !== t16 || $[90] !== t9) {
    t17 = <div className={t9}>{t10}{t11}{t12}{t16}</div>;
    $[86] = t10;
    $[87] = t11;
    $[88] = t12;
    $[89] = t16;
    $[90] = t9;
    $[91] = t17;
  } else {
    t17 = $[91];
  }
  let t18;
  if ($[92] !== activeMetric || $[93] !== provider.providerId) {
    t18 = metricTabs.map(tab => <button key={tab.key} id={`metric-tab-${provider.providerId}-${tab.key}`} role="tab" type="button" aria-selected={activeMetric === tab.key} aria-controls={`metric-panel-${provider.providerId}-${tab.key}`} className={cn("rounded-md px-3 py-1 text-xs font-medium transition-colors", activeMetric === tab.key ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground")} onClick={() => setActiveMetric(tab.key)}>{tab.label}</button>);
    $[92] = activeMetric;
    $[93] = provider.providerId;
    $[94] = t18;
  } else {
    t18 = $[94];
  }
  let t19;
  if ($[95] !== t18) {
    t19 = <div role="tablist" aria-label="Metric" className="flex items-center gap-1 border-b pb-2">{t18}</div>;
    $[95] = t18;
    $[96] = t19;
  } else {
    t19 = $[96];
  }
  let t20;
  if ($[97] !== T1 || $[98] !== t13 || $[99] !== t14 || $[100] !== t17 || $[101] !== t19) {
    t20 = <T1 className={t13}>{t14}{t17}{t19}</T1>;
    $[97] = T1;
    $[98] = t13;
    $[99] = t14;
    $[100] = t17;
    $[101] = t19;
    $[102] = t20;
  } else {
    t20 = $[102];
  }
  const t21 = `metric-panel-${provider.providerId}-${activeMetric}`;
  const t22 = `metric-tab-${provider.providerId}-${activeMetric}`;
  let t23;
  if ($[103] !== activeMetric || $[104] !== config || $[105] !== currentHourIndex || $[106] !== currentHourTimestamp || $[107] !== dataKeys || $[108] !== provider.logsCount || $[109] !== provider.points || $[110] !== provider.totalTokens || $[111] !== stacked) {
    t23 = provider.logsCount === 0 && provider.totalTokens === 0 ? <div className="flex h-[200px] items-center justify-center text-sm text-muted-foreground">No traffic in the last 24 hours</div> : <ChartContainer config={config} className="aspect-auto h-[200px] w-full"><BarChart data={provider.points} margin={{
        left: 0,
        right: 8,
        top: 4,
        bottom: 0
      }}><CartesianGrid vertical={false} strokeDasharray="3 3" /><XAxis dataKey="timestamp" tickLine={false} axisLine={false} tickMargin={8} minTickGap={24} tickFormatter={_temp} /><YAxis tickLine={false} axisLine={false} tickMargin={4} width={60} allowDecimals={false} tickFormatter={formatCompact} /><ChartTooltip cursor={{
          fill: "color-mix(in srgb, currentColor 8%, transparent)"
        }} content={<ChartTooltipContent labelFormatter={value_0 => value_0 === currentHourTimestamp ? `${formatHourRange(value_0)} (so far)` : formatHourRange(value_0)} valueFormatter={value_1 => activeMetric === "latency" ? `${Math.round(value_1)}ms` : formatNumber(value_1)} />} />{dataKeys.map((key, i) => <Bar key={key} dataKey={key} fill={`var(--color-${key})`} stackId={stacked ? activeMetric : undefined} radius={!stacked || i === dataKeys.length - 1 ? [3, 3, 0, 0] : [0, 0, 0, 0]} maxBarSize={stacked || dataKeys.length === 1 ? 28 : 14} isAnimationActive={false}>{provider.points.map((point, index) => <Cell key={point.timestamp} fillOpacity={index === currentHourIndex ? 0.45 : 1} />)}</Bar>)}</BarChart></ChartContainer>;
    $[103] = activeMetric;
    $[104] = config;
    $[105] = currentHourIndex;
    $[106] = currentHourTimestamp;
    $[107] = dataKeys;
    $[108] = provider.logsCount;
    $[109] = provider.points;
    $[110] = provider.totalTokens;
    $[111] = stacked;
    $[112] = t23;
  } else {
    t23 = $[112];
  }
  let t24;
  if ($[113] !== t21 || $[114] !== t22 || $[115] !== t23) {
    t24 = <CardContent id={t21} role="tabpanel" aria-labelledby={t22} className="px-2 pb-4 sm:px-6">{t23}</CardContent>;
    $[113] = t21;
    $[114] = t22;
    $[115] = t23;
    $[116] = t24;
  } else {
    t24 = $[116];
  }
  let t25;
  if ($[117] !== T2 || $[118] !== t20 || $[119] !== t24) {
    t25 = <T2>{t20}{t24}</T2>;
    $[117] = T2;
    $[118] = t20;
    $[119] = t24;
    $[120] = t25;
  } else {
    t25 = $[120];
  }
  return t25;
}
function _temp(value) {
  return format(new Date(value), "HH:mm");
}
function Stat(t0) {
  const $ = _c(13);
  const {
    icon: Icon,
    label,
    value,
    sub
  } = t0;
  let t1;
  if ($[0] !== Icon) {
    t1 = <Icon className="h-3 w-3" />;
    $[0] = Icon;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== label || $[3] !== t1) {
    t2 = <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-muted-foreground">{t1}{label}</div>;
    $[2] = label;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== value) {
    t3 = <div className="text-base font-semibold tabular-nums">{value}</div>;
    $[5] = value;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== sub) {
    t4 = sub && <div className="text-[11px] text-muted-foreground tabular-nums">{sub}</div>;
    $[7] = sub;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t2 || $[10] !== t3 || $[11] !== t4) {
    t5 = <div className="space-y-0.5">{t2}{t3}{t4}</div>;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  return t5;
}
export function ModelUptimeCharts({
  modelId
}: {
  modelId: string;
}) {
  const api = useApi();
  const {
    data,
    isLoading,
    isError,
    refetch
  } = api.useQuery("get", "/internal/models/{modelId}/uptime", {
    params: {
      path: {
        modelId
      }
    }
  }, {
    refetchInterval: 60_000,
    staleTime: 30_000
  });
  if (isLoading) {
    return <div className="space-y-6">
				{[1, 2, 3].map(i => <div key={i} className="h-[360px] animate-pulse rounded-lg border border-border bg-muted/30" />)}
			</div>;
  }
  if (isError) {
    return <Card>
				<CardContent className="flex h-40 flex-col items-center justify-center gap-3 text-sm text-muted-foreground">
					<AlertTriangle className="h-6 w-6 text-destructive" />
					<span>Unable to load uptime data. Please try again.</span>
					<Button variant="outline" size="sm" onClick={() => refetch()} className="gap-2">
						<RefreshCw className="h-3.5 w-3.5" />
						Retry
					</Button>
				</CardContent>
			</Card>;
  }
  if (!data || data.providers.length === 0) {
    return <Card>
				<CardContent className="flex h-40 flex-col items-center justify-center gap-2 text-sm text-muted-foreground">
					<Zap className="h-6 w-6" />
					No uptime data is available for this model in the last 24 hours.
				</CardContent>
			</Card>;
  }
  return <div className="space-y-6">
			{data.providers.map(provider => <ProviderUptimeCard key={provider.providerId} provider={provider} />)}
		</div>;
}